import {
    ApiResponseError,
    type ApiClient,
    type AuthPasswordChangeRequest,
    UnauthorizedApiRequestError
} from '@tadpoles/api-client'
import { getLocalTimeZone } from '@tadpoles/common'

import {
    StateTransitionNotifier,
    type StateTransitionSubscriber
} from '$lib/utils/state-transition-notifier.js'
import { createSessionContext, type SessionContext } from '../../context/session-context.js'
import { FamilyApplicationId } from '../app-identity.js'
import {
    getPasswordResetFailure,
    type PasswordResetFailure
} from '../password-reset/failure.js'
import type { FamilyLocalBacking } from '../persistence/family-local-backing.js'
import type { StoredAuthenticationContext } from './authentication-context.js'

export type AuthenticationMethodContext = {
    apiClient: ApiClient
    signal: AbortSignal
    timeZone: string
}

type AuthenticationMethodBase = {
    authenticate: (options: AuthenticationMethodContext) => Promise<void>
}

type DirectLoginMethod = AuthenticationMethodBase & {
    kind: 'direct'
}

type OAuthLoginMethod = AuthenticationMethodBase & {
    kind: 'oauth'
    provider?: string
}

export type AuthenticationLoginMethod = DirectLoginMethod | OAuthLoginMethod
export type AuthenticationLoginKind = 'direct' | 'oauth' | 'session-restored'

export type PasswordChangeRequest = AuthPasswordChangeRequest

export type DirectAuthenticationMethodOptions = {
    email: string
    password: string
    service?: string
    sessionLength: number
}

type GoogleAccessTokenAuthenticationOptions = {
    accessToken: string
}

type GoogleIdTokenAuthenticationOptions = {
    idToken: string
}

export type GoogleAuthenticationTokenOptions =
    | GoogleAccessTokenAuthenticationOptions
    | GoogleIdTokenAuthenticationOptions

export type GoogleAuthenticationMethodOptions = GoogleAuthenticationTokenOptions & {
    sessionLength: number
}

export const AppleAuthenticationBackendAppId = FamilyApplicationId

export class AuthenticationError extends Error {}

export type AppleAuthenticationMethodOptions = {
    authorizationCode: string
    redirectUri: string
    sessionLength: number
}

export function createDirectAuthenticationMethod(
    options: DirectAuthenticationMethodOptions
): DirectLoginMethod {
    return {
        kind: 'direct',
        authenticate: async ({ apiClient, signal, timeZone }) => {
            await apiClient.auth.login(
                {
                    email: options.email,
                    password: options.password,
                    service: options.service,
                    sessionLength: options.sessionLength,
                    tz: timeZone
                },
                { signal }
            )
        }
    }
}

export function createAppleAuthenticationMethod(
    options: AppleAuthenticationMethodOptions
): OAuthLoginMethod {
    return {
        kind: 'oauth',
        provider: 'apple',
        authenticate: async ({ apiClient, signal }) => {
            await apiClient.auth.loginWithAppleToken(
                {
                    appId: AppleAuthenticationBackendAppId,
                    authorizationCode: options.authorizationCode,
                    redirectUri: options.redirectUri,
                    sessionLength: options.sessionLength
                },
                {
                    signal
                }
            )
        }
    }
}

export function createGoogleAuthenticationMethod(
    options: GoogleAuthenticationMethodOptions
): OAuthLoginMethod {
    return {
        kind: 'oauth',
        provider: 'google',
        authenticate: async ({ apiClient, signal }) => {
            await apiClient.auth.loginWithGoogleToken(options, {
                signal
            })
        }
    }
}

export type LoggedOutAuthenticationReason =
    | 'account-deletion-completed'
    | 'account-deletion-manual-removal-completed'
    | 'account-deletion-manual-removal-required'
    | 'email-change-completed'
    | 'explicit-logout'
    | 'password-reset-completed'

export type AuthenticationFailureReason = 'invalid-credentials'

export type LoggedOutAuthenticationError =
    | {
          category: 'login'
          failureReason?: AuthenticationFailureReason
          loginKind: Exclude<AuthenticationLoginKind, 'session-restored'>
          message: string
      }
    | {
          category: 'session'
          message: string
          reason: 'expired' | 'restore-failed'
      }

export type LoggedOutAuthenticationState = {
    error?: LoggedOutAuthenticationError
    reason?: LoggedOutAuthenticationReason
    phase: 'logged-out'
}

export type LoggingInAuthenticationState = {
    loginKind: AuthenticationLoginKind
    phase: 'logging-in'
}

export type AdmittingAuthenticationState = {
    loginKind: AuthenticationLoginKind
    phase: 'admitting'
}

export type AuthenticatedAuthenticationState = {
    loginKind: AuthenticationLoginKind
    phase: 'authenticated'
    sessionContext: SessionContext
}

export type LoggingOutAuthenticationState = {
    phase: 'logging-out'
}

export type AuthenticationState =
    | LoggedOutAuthenticationState
    | LoggingInAuthenticationState
    | AdmittingAuthenticationState
    | AuthenticatedAuthenticationState
    | LoggingOutAuthenticationState

export type AuthenticationStateSubscriber = StateTransitionSubscriber<AuthenticationState>

export type AuthenticationBootstrapPhase = 'restoring' | 'ready'

export type PreservedSocialLoginNavigationState =
    | { phase: 'idle' }
    | { phase: 'pending' }
    | { phase: 'invalid-link' }

export type PasswordResetSessionAdmissionOptions = {
    signal?: AbortSignal
}

export type PasswordResetOperationOptions = {
    signal?: AbortSignal
}

export type PasswordResetOperationResult =
    | { status: 'succeeded' }
    | { failure: PasswordResetFailure; status: 'failed' }

const PasswordResetOperationSucceeded = { status: 'succeeded' } as const

export class AuthenticationService {
    readonly #apiClient: ApiClient
    readonly #localBacking: FamilyLocalBacking
    readonly #stateNotifier = new StateTransitionNotifier<AuthenticationState>(() => this.state)
    #authenticationContextWrite: Promise<unknown> = Promise.resolve()
    #operationController = new AbortController()
    #validatedPasswordResetToken: string | undefined
    #restorePromise: Promise<void> | undefined
    #preservedSocialLoginNavigation: PreservedSocialLoginNavigationState = $state({
        phase: 'idle'
    })
    public bootstrapPhase = $state<AuthenticationBootstrapPhase>('restoring')
    #state: AuthenticationState = $state({
        phase: 'logged-out'
    })

    public constructor(apiClient: ApiClient, localBacking: FamilyLocalBacking) {
        this.#apiClient = apiClient
        this.#localBacking = localBacking
    }

    public get state(): AuthenticationState {
        return this.#state
    }

    public set state(state: AuthenticationState) {
        const previous = this.#state
        this.#state = state
        this.#stateNotifier.notify(this.#state, previous)
    }

    public getState = (): AuthenticationState => this.state

    public subscribe = this.#stateNotifier.subscribe

    public get preservedSocialLoginNavigation(): PreservedSocialLoginNavigationState {
        return this.#preservedSocialLoginNavigation
    }

    public beginPreservedSocialLoginNavigation(): void {
        this.#preservedSocialLoginNavigation = { phase: 'pending' }
    }

    public completePreservedSocialLoginNavigation(): void {
        this.#preservedSocialLoginNavigation = { phase: 'idle' }
    }

    public rejectPreservedSocialLoginNavigation(): void {
        if (this.#preservedSocialLoginNavigation.phase !== 'pending') {
            return
        }

        this.#preservedSocialLoginNavigation = { phase: 'invalid-link' }
    }

    public dismissInvalidPreservedLinkNotice(): void {
        if (this.#preservedSocialLoginNavigation.phase !== 'invalid-link') {
            return
        }

        this.#preservedSocialLoginNavigation = { phase: 'idle' }
    }

    public reportOAuthLoginFailure(): void {
        if (this.state.phase !== 'logged-out') {
            return
        }

        this.#setState({
            error: {
                category: 'login',
                loginKind: 'oauth',
                message: this.#toErrorMessage(undefined)
            },
            phase: 'logged-out'
        })
    }

    public dismissAuthenticationError(): void {
        if (this.state.phase !== 'logged-out' || this.state.error === undefined) {
            return
        }

        this.#setState({ phase: 'logged-out' })
    }

    public consumeExplicitLogoutRedirect = (): boolean =>
        this.#clearLoggedOutReason('explicit-logout')

    public dismissEmailChangeCompletionNotice = (): boolean =>
        this.#clearLoggedOutReason('email-change-completed')

    public dismissAccountDeletionCompletionNotice = (): boolean =>
        this.#clearLoggedOutReason('account-deletion-completed')

    public dismissPasswordResetCompletionNotice = (): boolean =>
        this.#clearLoggedOutReason('password-reset-completed')

    public get validatedPasswordResetToken(): string | undefined {
        return this.#validatedPasswordResetToken
    }

    public setValidatedPasswordResetToken(token: string): void {
        const normalizedToken = token.trim()
        this.#validatedPasswordResetToken = normalizedToken || undefined
    }

    public clearValidatedPasswordResetToken(): void {
        this.#validatedPasswordResetToken = undefined
    }

    public requestPasswordReset = async (
        email: string,
        options: PasswordResetOperationOptions = {}
    ): Promise<PasswordResetOperationResult> =>
        this.#runPasswordResetOperation(
            () => this.#apiClient.auth.requestPasswordReset({ email }, options),
            options.signal
        )

    public validatePasswordResetToken = async (
        token: string,
        options: PasswordResetOperationOptions = {}
    ): Promise<PasswordResetOperationResult> =>
        this.#runPasswordResetOperation(
            () => this.#apiClient.auth.validatePasswordResetToken(token, options),
            options.signal
        )

    public resetPassword = async (
        newPassword: string,
        token: string,
        options: PasswordResetOperationOptions = {}
    ): Promise<PasswordResetOperationResult> =>
        this.#runPasswordResetOperation(
            () => this.#apiClient.auth.resetPassword({ newPassword, token }, options),
            options.signal
        )

    public restore = async (): Promise<void> => {
        this.#restorePromise ??= this.#restoreAuthenticationContext()
        await this.#restorePromise
    }

    async #runPasswordResetOperation(
        operation: () => Promise<unknown>,
        signal?: AbortSignal
    ): Promise<PasswordResetOperationResult> {
        try {
            await operation()
            return PasswordResetOperationSucceeded
        } catch (error) {
            if (signal?.aborted) {
                throw error
            }

            return {
                failure: getPasswordResetFailure(error),
                status: 'failed'
            }
        }
    }

    public login = async (method: AuthenticationLoginMethod): Promise<void> => {
        const operationController = this.#replaceOperationController()
        const timeZone = getLocalTimeZone()
        let authenticationCompleted = false
        this.completePreservedSocialLoginNavigation()
        this.#setState({
            phase: 'logging-in',
            loginKind: method.kind
        })

        try {
            await this.#runLogin(method, timeZone, operationController.signal)
            authenticationCompleted = true

            this.#assertCurrentOperation(operationController)
            this.#setState({
                phase: 'admitting',
                loginKind: method.kind
            })

            await this.#admitSession(method.kind, timeZone, operationController)
        } catch (error) {
            if (operationController.signal.aborted) {
                return
            }

            this.#assertCurrentOperation(operationController)
            await this.#clearAuthenticationContext()
            const failureReason = authenticationCompleted
                ? undefined
                : this.#getFailureReason(method.kind, error)
            this.#setState({
                phase: 'logged-out',
                error: {
                    category: 'login',
                    ...(failureReason === undefined ? {} : { failureReason }),
                    loginKind: method.kind,
                    message: this.#toErrorMessage(error)
                }
            })
        }
    }

    public admitSessionAfterPasswordReset = async (
        options: PasswordResetSessionAdmissionOptions = {}
    ): Promise<boolean> => {
        const operationController = this.#replaceOperationController()
        const abortOperation = () => operationController.abort()

        await this.#clearAuthenticationContext()

        if (options.signal?.aborted) {
            return false
        }

        options.signal?.addEventListener('abort', abortOperation, { once: true })

        try {
            await this.#admitSession(
                'session-restored',
                getLocalTimeZone(),
                operationController
            )
            this.#assertCurrentOperation(operationController)
            return this.state.phase === 'authenticated'
        } catch {
            if (operationController.signal.aborted) {
                return false
            }

            this.#assertCurrentOperation(operationController)
            this.#setState({
                phase: 'logged-out',
                reason: 'password-reset-completed'
            })
            return false
        } finally {
            options.signal?.removeEventListener('abort', abortOperation)
        }
    }

    public logout = async (): Promise<void> => {
        const operationController = this.#replaceOperationController()
        this.#apiClient.abortAllRequests()
        this.completePreservedSocialLoginNavigation()
        this.#setState({
            phase: 'logging-out'
        })

        void this.#sendBackendLogout(operationController)
        await this.#clearAuthenticationContext()

        if (operationController.signal.aborted) {
            return
        }

        this.#setState({
            reason: 'explicit-logout',
            phase: 'logged-out'
        })
    }

    public changePassword = async (request: PasswordChangeRequest): Promise<void> => {
        try {
            await this.#apiClient.auth.changePassword(request)
        } catch (error) {
            if (error instanceof UnauthorizedApiRequestError) {
                await this.expireSession()
            }

            throw error
        }
    }

    public completeEmailChange = async (): Promise<void> => {
        await this.#completeAccountMutation('email-change-completed')
    }

    public beginAccountDeletionCompletion = async (): Promise<boolean> => {
        this.#replaceOperationController()
        this.#apiClient.abortAllRequests()
        this.#setState({
            phase: 'logged-out',
            reason: 'account-deletion-manual-removal-required'
        })
        return await this.#clearAuthenticationContext()
    }

    public finishAccountDeletionCompletion(cleanupSucceeded: boolean): void {
        if (
            this.state.phase !== 'logged-out' ||
            this.state.reason !== 'account-deletion-manual-removal-required'
        ) {
            return
        }

        this.#setState({
            phase: 'logged-out',
            reason: cleanupSucceeded
                ? 'account-deletion-completed'
                : 'account-deletion-manual-removal-completed'
        })
    }

    async #completeAccountMutation(reason: LoggedOutAuthenticationReason): Promise<void> {
        this.#replaceOperationController()
        this.#apiClient.abortAllRequests()
        this.#setState({
            phase: 'logged-out',
            reason
        })
        await this.#clearAuthenticationContext()
    }

    public expireSession = async (): Promise<void> => {
        const operationController = this.#replaceOperationController()
        this.#apiClient.abortAllRequests()
        this.#setState({
            phase: 'logged-out',
            error: {
                category: 'session',
                message: 'You have been logged out due to inactivity.',
                reason: 'expired'
            }
        })
        await this.#clearAuthenticationContext()

        if (operationController.signal.aborted) {
            return
        }
    }

    async #runLogin(
        method: AuthenticationLoginMethod,
        timeZone: string,
        signal: AbortSignal
    ): Promise<void> {
        await method.authenticate({
            apiClient: this.#apiClient,
            signal,
            timeZone
        })
    }

    async #sendBackendLogout(operationController: AbortController): Promise<void> {
        try {
            await this.#apiClient.auth.logout({
                signal: operationController.signal
            })
        } catch {
            return
        }
    }

    #replaceOperationController(): AbortController {
        this.#operationController.abort()
        this.#operationController = new AbortController()
        return this.#operationController
    }

    #assertCurrentOperation(operationController: AbortController): void {
        if (
            this.#operationController !== operationController ||
            operationController.signal.aborted
        ) {
            throw new DOMException('Authentication operation superseded.', 'AbortError')
        }
    }

    #setState(state: AuthenticationState): void {
        this.state = state
    }

    #clearLoggedOutReason(reason: LoggedOutAuthenticationReason): boolean {
        if (this.state.phase !== 'logged-out' || this.state.reason !== reason) {
            return false
        }

        this.#setState({
            phase: 'logged-out'
        })
        return true
    }

    #toErrorMessage(error: unknown): string {
        if (error instanceof AuthenticationError) {
            return error.message
        }

        return 'Authentication request failed.'
    }

    #getFailureReason(
        loginKind: AuthenticationLoginKind,
        error: unknown
    ): AuthenticationFailureReason | undefined {
        if (loginKind !== 'direct' || !(error instanceof ApiResponseError)) {
            return undefined
        }

        if (error.error?.code === '403' && error.error.message === 'Authentication Failed') {
            return 'invalid-credentials'
        }

        return undefined
    }

    async #persistAuthenticationContext(
        authenticationContext: StoredAuthenticationContext,
        operationController: AbortController
    ): Promise<void> {
        await this.#writeAuthenticationContext(async () => {
            if (operationController.signal.aborted) {
                return
            }

            const authenticationContextStore =
                await this.#localBacking.getAuthenticationContextStore()

            if (authenticationContextStore === undefined) {
                return
            }

            await authenticationContextStore.put(authenticationContext)
        }, 'Unable to persist authentication context.')
    }

    async #clearAuthenticationContext(): Promise<boolean> {
        return await this.#writeAuthenticationContext(async () => {
            const authenticationContextStore =
                await this.#localBacking.getAuthenticationContextStore()

            if (authenticationContextStore === undefined) {
                return
            }

            await authenticationContextStore.clear()
        }, 'Unable to clear authentication context.')
    }

    async #writeAuthenticationContext(
        write: () => Promise<void>,
        warningMessage: string
    ): Promise<boolean> {
        const writePromise = this.#authenticationContextWrite.then(async () => {
            try {
                await write()
                return true
            } catch (error) {
                console.warn(warningMessage, error)
                return false
            }
        })
        this.#authenticationContextWrite = writePromise.catch(() => undefined)
        return await writePromise
    }

    async #restoreAuthenticationContext(): Promise<void> {
        try {
            try {
                const authenticationContextStore =
                    await this.#localBacking.getAuthenticationContextStore()

                const authenticationContext = await authenticationContextStore?.get()

                if (authenticationContext) {
                    this.#setState({
                        phase: 'authenticated',
                        loginKind: authenticationContext.loginKind,
                        sessionContext: createSessionContext(authenticationContext.namespace)
                    })
                    return
                }
            } catch (error) {
                console.warn('Unable to restore authentication context.', error)
            }

            await this.#restoreBackendSession()
        } finally {
            this.bootstrapPhase = 'ready'
            this.#restorePromise = undefined
        }
    }

    async #restoreBackendSession(): Promise<void> {
        const operationController = this.#replaceOperationController()
        this.#setState({
            phase: 'admitting',
            loginKind: 'session-restored'
        })

        try {
            await this.#admitSession(
                'session-restored',
                getLocalTimeZone(),
                operationController
            )
        } catch (error) {
            if (operationController.signal.aborted) {
                return
            }

            this.#assertCurrentOperation(operationController)
            this.#setState(
                error instanceof UnauthorizedApiRequestError
                    ? { phase: 'logged-out' }
                    : {
                          phase: 'logged-out',
                          error: {
                              category: 'session',
                              message: this.#toErrorMessage(error),
                              reason: 'restore-failed'
                          }
                      }
            )
        }
    }

    async #admitSession(
        loginKind: AuthenticationLoginKind,
        timeZone: string,
        operationController: AbortController
    ): Promise<void> {
        const admitResponse = await this.#apiClient.auth.admit(timeZone, {
            signal: operationController.signal
        })

        this.#assertCurrentOperation(operationController)
        const authenticationContext = {
            loginKind,
            namespace: admitResponse.namespace,
            authenticatedAt: Date.now()
        }
        this.#setState({
            phase: 'authenticated',
            loginKind,
            sessionContext: createSessionContext(admitResponse.namespace)
        })
        void this.#persistAuthenticationContext(authenticationContext, operationController)
    }
}
